import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useDataRefresh } from "@/hooks/useDataRefresh";

interface ChunkFallbackProps {
  error?: Error;
  retry?: () => void;
  onReload?: () => void;
}

export function ChunkFallback({ error, retry, onReload }: ChunkFallbackProps) {
  const { refreshCurrentRoute, fullRefresh } = useDataRefresh();
  const isChunkError = error?.message?.includes('Loading chunk') || 
                      error?.message?.includes('Failed to fetch dynamically imported module');

  const handleReload = async () => {
    if (onReload) {
      onReload();
      return;
    }
    
    try {
      // Try route-based refresh first for chunk errors
      if (isChunkError) {
        refreshCurrentRoute();
      } else {
        await fullRefresh();
      }
    } catch (refreshError) {
      console.warn('⚠️ React-based refresh failed, using navigation helper fallback');
      const { useNavigationHelper } = await import('@/hooks/useNavigationHelper');
      const { safeReload } = useNavigationHelper();
      safeReload('chunk-fallback-reload');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                {isChunkError ? 'Loading Error' : 'Something went wrong'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isChunkError 
                  ? 'Failed to load application resources. This may be due to a network issue or an outdated version.'
                  : 'An unexpected error occurred while loading this page.'
                }
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full">
              {retry && (
                <Button 
                  variant="outline" 
                  onClick={retry}
                  className="flex-1"
                  size="sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              )}
              
              <Button 
                onClick={handleReload}
                className="flex-1"
                size="sm"
              >
                Reload Page
              </Button>
            </div>

            {error && import.meta.env.DEV && (
              <details className="w-full mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer">
                  Error Details
                </summary>
                <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-auto">
                  {error.message}
                </pre>
              </details>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}